import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import '../../css/db-backups.css';

const ZONE = 'Europe/Warsaw';

const formatDateTime = (iso) => {
   if (!iso) return '—';
   const dt = DateTime.fromISO(String(iso)).setZone(ZONE);
   return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm:ss') : String(iso);
};

// One attempt log per planned test-generation event (PlanedStudentEvent) -
// every scheduled or manual run the backend made, success or failure, so a
// teacher can see why a specific student's test didn't show up without
// digging through the general system log. Same status-pill vocabulary
// (student-lessons-badge .on/.off-explicit) this modal already uses for
// isCycled above it - green/red, nothing new to style.
export default function PlannedEventRuns({ eventId }) {
   const navigate = useNavigate();
   const plang = language[language.lang].plannedEvents;
   const [runs, setRuns] = useState([]);
   const [status, setStatus] = useState('loading');
   const [expanded, setExpanded] = useState(() => new Set());

   useEffect(() => {
      if (!eventId) return;
      setStatus('loading');
      setExpanded(new Set());
      axios.get('/api/student/plannedevents/' + eventId + '/runs', { params: { limit: 50 }, withCredentials: true })
         .then((response) => {
            setRuns(Array.isArray(response.data) ? response.data : []);
            setStatus('done');
         })
         .catch((err) => {
            if (err.response && err.response.status === 401) navigate('/login');
            setStatus('error');
         });
   }, [eventId, navigate]);

   const toggleExpanded = (id) => {
      setExpanded((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id); else next.add(id);
         return next;
      });
   };

   const openTest = (conversationProgressId) => navigate('/messages/byconvprogress/' + conversationProgressId);

   return (
      <div className='planned-runs'>
         <p className='student-card-section-title'>{plang.runsTitle}</p>
         {status === 'loading' ? <p className='lesson-detail-empty'>{language[language.lang]['Loading ...']}</p> : ''}
         {status === 'error' ? <p className='lesson-detail-empty planned-runs-error'>{plang.runLoadError}</p> : ''}
         {status === 'done' && runs.length === 0 ? <p className='lesson-detail-empty'>{plang.runsEmpty}</p> : ''}
         {status === 'done' && runs.length > 0 ? (
            <div className='planned-runs-list'>
               {runs.map((run) => (
                  <div className='planned-run-row' key={run.id}>
                     <div className='planned-run-top'>
                        <span className='planned-run-time'>{formatDateTime(run.startedAt)}</span>
                        <span className={'student-lessons-badge' + (run.status === 'SUCCESS' ? ' on' : ' off-explicit')}>
                           <i className='student-lessons-badge-dot'></i>{run.status === 'SUCCESS' ? plang.runSuccess : plang.runFailed}
                        </span>
                        {run.notificationError ? (
                           <span className='planned-run-notification-warn' title={run.notificationError}>
                              {plang.runNotificationIssue}
                           </span>
                        ) : ''}
                     </div>
                     <p className='planned-run-message'>{run.message}</p>
                     <div className='planned-run-actions'>
                        {run.status === 'SUCCESS' && run.conversationProgressId ? (
                           <span className='db-bk-link planned-run-link' onClick={() => openTest(run.conversationProgressId)}>
                              {plang.runViewTest}
                           </span>
                        ) : ''}
                        {run.errorDetails ? (
                           <span className='db-bk-link planned-run-link' onClick={() => toggleExpanded(run.id)}>
                              {expanded.has(run.id) ? plang.runHideDetails : plang.runShowDetails}
                           </span>
                        ) : ''}
                     </div>
                     {run.errorDetails && expanded.has(run.id) ? (
                        <pre className='db-bk-log planned-run-details'>{run.errorDetails}</pre>
                     ) : ''}
                  </div>
               ))}
            </div>
         ) : ''}
      </div>
   );
}
